import { Form } from "components/Form/Form";
import { useCostLinesStore } from "entities/CostLines";
import { useCostTypeOptions } from "entities/CostType";
import {
  RequestAccessController,
  requestService,
  useRequestDependencies,
  useRequestPersistedStore,
  useRequestStore,
} from "entities/Request";
import { useRequestLinesStore } from "entities/TravelLines";
import { useInternalOrderStore } from "features/SelectInternalOrder";
import { transactionCostLineDefaultValues } from "modules/CardStatement/constants/transactionCostLineDefaultValues";
import { getCardCostLineOnSaveValidationSchema } from "modules/CardStatement/validations/getCardCostLineOnSaveValidationSchema";
import { getCardCostLineOnSubmitValidationSchema } from "modules/CardStatement/validations/getCardCostLineOnSubmitValidationSchema";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  toVatCodesOptionsTransformer,
  toVatLabelTransformer,
} from "shared/lib";
import TransactionCostLineView from "./TransactionCostLine.view";
import { toGlCodesOptionsTransformer } from "shared/lib/transformers/toGlCodesOptions.transformer";

const access = new RequestAccessController();

const TransactionCostLineContainer = forwardRef(
  function TransactionCostLineContainer(props, ref) {
    const {
      // @ts-ignore
      idx,
      // @ts-ignore
      costLineData,
      // @ts-ignore
      cardFormat,
      // @ts-ignore
      transactionId,
      // @ts-ignore
      parentCostSplitIdx,
      // @ts-ignore
      requestIsEditable,
    } = props;
    const updateCostLineState = useCostLinesStore(
      (state) => state.updateCostLineState
    );
    const setRequest = useRequestStore((state) => state.setRequest);
    const removeSplittedCardRequestCost = useRequestStore(
      (state) => state.removeSplittedCardRequestCost
    );
    const viewRequestAs = useRequestPersistedStore(
      (state) => state.viewRequestAs
    );
    const splitCardRequestCost = useRequestStore(
      (state) => state.splitCardRequestCost
    );
    const request = useRequestStore((state) => state.request);
    const internalOrder = useInternalOrderStore((state) => state.internalOrder);
    const [vatCodes, setVatCodes] = useState([]);
    const [glCodes, setGlCodes] = useState([]);

    const { data: { validations, dependencies } = {} } = useRequestDependencies(
      "credit_cards_form_dependencies",
      request?.id,
      {
        triggerRequest: access.to("SubCosts", "edit").provided(),
      }
    );

    const [onSaveResolver, onSubmitResolver] = useMemo(
      () => [
        getCardCostLineOnSaveValidationSchema(cardFormat),
        getCardCostLineOnSubmitValidationSchema(cardFormat),
      ],
      [cardFormat]
    );

    const resolver = useRef(onSaveResolver);

    const costLineForm = useForm({
      resolver: (...args) => resolver.current(...args),
      defaultValues: transactionCostLineDefaultValues,
    });

    const {
      register,
      reset,
      watch,
      handleSubmit,
      getValues,
      resetField,
      setValue,
      unregister,
      formState: { isDirty, errors, dirtyFields },
    } = costLineForm;

    const costType = watch("cost_type");
    const glCode = watch("posting_gl_code");

    useEffect(() => {
      updateCostLineState(costLineData.id, {
        isModified: isDirty || costLineData.id.toString().startsWith("initial"),
        hasErrors: !!Object.keys(errors).length,
      });
    }, [isDirty, errors, costLineData]);

    const addRequestLineToStack = useRequestLinesStore(
      (state) => state.addRequestLineToStack
    );

    const handleRequestLineSave = () => {
      addRequestLineToStack(
        new Promise((resolve) => {
          handleSubmit(
            // @ts-ignore
            ({ cost_type, amount, vat, posting_gl_code, ...formData }) => {
              const data = {
                ...costLineData,
                ...formData,
                lineType: request?.type,
                amount: parseFloat(amount.replaceAll(" ", "")),
                vat_id: vat?.value ?? null,
                credit_cards_cost_types_id: Number(cost_type?.value?.id),
                posting_gl_code: posting_gl_code?.value ?? null,
                isDirty:
                  costLineData.id.toString().startsWith("initial") || isDirty,
              };
              resolve({
                isValid: true,
                formData: data,
              });
            },
            (errors) => resolve({ isValid: false, errors })
          )();
        })
      );
    };

    useImperativeHandle(ref, () => ({
      costLineSave() {
        resolver.current = onSaveResolver;
        handleRequestLineSave();
      },
      costLineSubmit() {
        resolver.current = onSubmitResolver;
        handleRequestLineSave();
      },
    }));

    useEffect(() => {
      if (costLineData?.id) {
        setVatCodes(costLineData?.cost_type?.available_vat_codes?.data ?? []);
        const formValues = Object.fromEntries(
          Object.entries(transactionCostLineDefaultValues).map(
            ([field, value]) => {
              let newValue = costLineData?.[field] ?? value;
              if (field === "posting_gl_code") {
                if (newValue) {
                  newValue = {
                    value: costLineData?.posting_gl_code,
                    label: `${costLineData?.posting_gl_code} ${
                      costLineData?.posting_gl_code_description
                        ? `- ${costLineData?.posting_gl_code_description}`
                        : ""
                    }`,
                  };
                }
              }
              if (field === "cost_type") {
                if (newValue?.data?.id) {
                  newValue = {
                    value: {
                      id: newValue.data.id,
                      subtype: newValue.data.subtype,
                    },
                    label: newValue?.data?.name,
                  };
                  // ! Empty cost type is returned as an empty array
                } else newValue = transactionCostLineDefaultValues.cost_type;
              }
              if (field === "attachments")
                newValue = costLineData?.attachments?.data?.map(
                  (fileData) => fileData.id
                );
              return [field, newValue];
            }
          )
        );
        reset(formValues);
      }
    }, [costLineData]);

    useEffect(() => {
      if (
        internalOrder?.label &&
        getValues("posting_internal_order") !== internalOrder?.label
      )
        setValue("posting_internal_order", internalOrder?.label, {
          shouldDirty: true,
        });
    }, [internalOrder?.label]);

    const isJobRelated = watch("is_job_related");

    const costTypeOptions = useCostTypeOptions({
      costTypes: dependencies?.credit_cards_cost_types?.data,
      costLineData,
      isJobRelated,
    });

    useEffect(() => {
      if (isJobRelated) unregister("private_cost");
    }, [isJobRelated]);

    useEffect(() => {
      const costTypeData = dependencies?.credit_cards_cost_types?.data?.find(
        (ct) => ct?.id === costType?.value?.id
      );

      setVatCodes(costTypeData?.available_vat_codes?.data ?? []);
      setGlCodes(costTypeData?.gl_codes?.data ?? []);

      if (costTypeData) {
        if (costType?.value?.id !== costLineData?.credit_cards_cost_types_id) {
          if (
            costTypeData?.available_vat_codes?.data?.length === 1 &&
            !isJobRelated
          ) {
            const firstVatCode = costTypeData?.available_vat_codes?.data[0];
            const formattedVatCode = {
              value: firstVatCode.id,
              label: toVatLabelTransformer(firstVatCode),
            };
            setValue("vat", formattedVatCode);
          } else {
            resetField("vat", {
              defaultValue: transactionCostLineDefaultValues.vat,
            });
          }
        }
      }
    }, [costType?.value?.id, costLineData, dependencies, isJobRelated]);

    useEffect(() => {
      if (!dirtyFields?.cost_type || (glCode && !costType)) return;

      if (costType) {
        if (glCodes?.length === 1) {
          const firstGlCode = glCodes[0];

          const formattedGlCode = {
            value: firstGlCode.number,
            label: `${firstGlCode.number} - ${firstGlCode.description}`,
          };
          setValue("posting_gl_code", formattedGlCode);
        } else {
          resetField("posting_gl_code", {
            defaultValue: transactionCostLineDefaultValues.posting_gl_code,
          });
        }
      }
    }, [costType, glCodes]);

    const handleCostSplit = () => {
      const { cost_type, ...currentValues } = getValues();
      const devidedAmount = (
        parseFloat(
          typeof currentValues.amount === "string"
            ? currentValues.amount.replaceAll(" ", "")
            : currentValues.amount
        ) / 2
      ).toFixed(2);
      splitCardRequestCost(transactionId, idx + 1, {
        ...transactionCostLineDefaultValues,
        ...currentValues,
        amount: devidedAmount,
        attachments: { data: [] },
        cost_type: {
          data: dependencies.credit_cards_cost_types.data.find(
            (costType) => costType.id === cost_type?.value?.id
          ),
        },
        id: `initial${Math.random()}`,
        posting_gl_code: currentValues?.posting_gl_code?.value ?? null,
        costs_transaction_id: costLineData.costs_transaction_id,
        credit_cards_cost_types_id: cost_type?.value?.id,
        splitted_cost_id: costLineData.splitted_cost_id
          ? costLineData.splitted_cost_id
          : costLineData.id,
      });
      setValue("amount", devidedAmount, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    const [isRemoving, setIsRemoving] = useState(false);

    const handleSplittedCostRemoval = () => {
      if (costLineData.id.toString().startsWith("initial")) {
        removeSplittedCardRequestCost(transactionId, costLineData.id);
      } else {
        setIsRemoving(true);
        requestService
          .updateCardRequest({
            application_number: request.number,
            user_role: viewRequestAs.role,
            credit_cards_costs_transaction_sub_costs_to_delete: [
              { id: costLineData.id },
            ],
          })
          .then((requestData) => setRequest(requestData))
          .finally(() => setIsRemoving(false));
      }
    };

    const uploadedFiles = useMemo(
      () =>
        costLineData.attachments?.data.map((attachment) => ({
          ...attachment,
          isUploaded: true,
        })),
      [costLineData.attachments?.data]
    );

    const isCostTypeSelectDisabled =
      !requestIsEditable || (isJobRelated && costTypeOptions.length === 1);

    return (
      <div className="p-5 sub-cost-container">
        <h6 className="fw-bolder mb-4">
          {`#${idx + 1}.`}{" "}
          {`${
            parentCostSplitIdx !== null
              ? `split from #${parentCostSplitIdx + 1}`
              : ""
          }`}
        </h6>
        <Form form={costLineForm} onSubmit={(data) => console.log(data)}>
          <TransactionCostLineView
            idx={idx}
            errors={errors}
            setValue={setValue}
            costLineData={costLineData}
            currencyCode={
              isJobRelated
                ? request?.local_currency
                : costLineData?.currency_code
            }
            isJobRelated={isJobRelated}
            isRemoving={isRemoving}
            cardFormat={cardFormat}
            register={register}
            costTypeOptions={costTypeOptions}
            isChildOfSplittedCost={costLineData?.splitted_cost_id !== null}
            uploadedFiles={uploadedFiles}
            dependencies={dependencies}
            validations={validations}
            handleCostSplit={handleCostSplit}
            handleSplittedCostRemoval={handleSplittedCostRemoval}
            requestIsEditable={requestIsEditable}
            costSplitIsEnabled={
              ((costType?.label === "To be billed back" && requestIsEditable) ||
                cardFormat === "corporate") &&
              requestIsEditable
            }
            vatCodes={toVatCodesOptionsTransformer(vatCodes)}
            glCodes={toGlCodesOptionsTransformer(glCodes)}
            isJobRelatedAllowed={request.is_job_related_allowed}
            isCostTypeSelectDisabled={isCostTypeSelectDisabled}
            isPrivateCostAllowed={cardFormat === "corporate" && !isJobRelated}
          />
        </Form>
      </div>
    );
  }
);

export default memo(TransactionCostLineContainer);
